import {
  UploadJob, UploadEntry, runUpload, saveJob, deleteJob, pendingJobs, enumerateHandle,
} from '../components/FileLibrary/folderUpload';

// ── Global folder-upload driver (owner 2026-08-24) ──────────────────────────────────────────────
// "不管在什么页面，都可以继续上传".
//
// The engine used to be driven from inside <FileLibrary>, so leaving the page unmounted the
// component and the upload simply stopped — the owner lost a 3,200-file job that way: they refreshed
// onto Document Storage, clicked through to Email 15 seconds later, and the resume never ran.
// State lives at MODULE level instead: routing swaps components, it does not re-evaluate modules, so
// a job started here keeps going across every navigation and only ends with the tab.
//
// Deliberately NOT a React context: a context still hangs off a component tree that a route change
// can unmount. A plain module + subscribe() is what actually survives.

export type UploadProgress = {
  jobId: string; rootName: string;
  done: number; total: number; current: string;
  running: boolean; failed: number;
};

let prog: UploadProgress | null = null;
let cancelled = false;
let running = false;
const subs = new Set<(p: UploadProgress | null) => void>();

const emit = () => { subs.forEach(f => { try { f(prog); } catch { /* a bad subscriber must not stop the upload */ } }); };

/** Subscribe to progress. Fires immediately with the current state; returns an unsubscribe. */
export function subscribe(fn: (p: UploadProgress | null) => void): () => void {
  subs.add(fn); fn(prog);
  return () => { subs.delete(fn); };
}
export const currentProgress = () => prog;
export const isRunning = () => running;
export const cancel = () => { cancelled = true; };
export const dismiss = () => { if (!running) { prog = null; emit(); } };

/** Run a job to completion. Only one at a time — a second call while busy is ignored. */
export async function start(job: UploadJob, entries: UploadEntry[]): Promise<void> {
  if (running) return;
  running = true; cancelled = false;
  prog = { jobId: job.id, rootName: job.rootName, done: job.done.length, total: job.total, current: '', running: true, failed: job.failed.length };
  emit();
  try {
    await saveJob(job);
    await runUpload(job, entries, {
      onProgress: (done, total, current) => {
        if (prog && prog.jobId === job.id) { prog = { ...prog, done, total, current }; emit(); }
      },
      isCancelled: () => cancelled,
    });
  } catch (e: any) {
    if (prog && prog.jobId === job.id) { prog = { ...prog, current: String(e?.message || e).slice(0, 160) }; }
  } finally {
    running = false;
    const complete = job.done.length >= job.total;
    if (complete) { try { await deleteJob(job.id); } catch { /* the banner query tolerates a stale row */ } }
    if (prog && prog.jobId === job.id) { prog = { ...prog, running: false, failed: job.failed.length }; emit(); }
    // A clean finish clears itself; one that left failures stays on screen until dismissed, so the
    // count of skipped files is actually seen.
    if (complete && !job.failed.length) setTimeout(() => { if (prog?.jobId === job.id && !running) { prog = null; emit(); } }, 4000);
    doneHooks.forEach(f => { try { f(); } catch { /* ignore */ } });
  }
}

// Anything that wants to refresh after an upload finishes (the file list, the resume banner).
const doneHooks = new Set<() => void>();
export function onFinished(fn: () => void): () => void { doneHooks.add(fn); return () => { doneHooks.delete(fn); }; }

// ── Auto-resume ────────────────────────────────────────────────────────────────────────────────
// Runs once per tab, from the app shell, so an interrupted job picks itself back up wherever the
// operator happens to be. The browser is the one limit we cannot design away: the File System
// Access API re-grants directory access only under a user gesture, so if the permission has lapsed
// we arm a one-shot listener and the operator's very next click ANYWHERE resumes it — there is no
// button to find. `pointerdown` in the capture phase counts as activation for requestPermission().
let armed = false;

export async function armAutoResume(): Promise<void> {
  if (armed || running) return;
  armed = true;
  let jobs: UploadJob[] = [];
  try { jobs = await pendingJobs(); } catch { return; }
  const job = jobs.find(j => j.dirHandle?.queryPermission);   // a folder-INPUT job needs a re-pick; its banner handles that
  if (!job) return;

  const go = async () => {
    try {
      const entries = await enumerateHandle(job.dirHandle, job.dirHandle.name, []);
      if (entries.length) await start(job, entries);
    } catch { /* folder moved or unreadable — the banner stays for a manual re-pick */ }
  };

  try {
    if (await job.dirHandle.queryPermission({ mode: 'read' }) === 'granted') { void go(); return; }
  } catch { return; }

  const onGesture = () => {
    document.removeEventListener('pointerdown', onGesture, true);
    void (async () => {
      try { if (await job.dirHandle.requestPermission({ mode: 'read' }) === 'granted') await go(); }
      catch { /* the operator declined */ }
    })();
  };
  document.addEventListener('pointerdown', onGesture, true);
}
